import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../styles/SmallChat.css';
import { ChatIcon, ExitScreen, IconButton, Typewriter, SubdivisionAccordion, PredictiveSearch, RedfinMarketTrends, FloodZoneAnalysis, ConstructionMaterialAnalysis, SubdivisionAnalysis, PriceCeilingShowcase, SubjectPropertyAnalysis, BasementAnalysis, CompsDistribution } from './';
import { Exit, Sparkle, Wand, Arrow } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { HugeiconsIcon } from '@hugeicons/react';
import { GuestHouseIcon } from '@hugeicons/core-free-icons';
import { renderHTML, renderHTMLAdjustments } from '../functions';

function SmallChat(props) {
    const collapsed = props.collapsed;
    const arvMessages = props.arvMessages;
    const loading = props.loading;
    const textAreaHeight = props.textAreaHeight;
    const arvLoading = props.arvLoading;
    const typeOutText = props.typeOutText;
    const setTypeOutText = props.setTypeOutText;
    const readOnly = props.readOnly;
    const arvDescriptionPrompt = props.arvDescriptionPrompt;
    const onChangePrompt = props.onChangePrompt;
    const submitPrompt = props.submitPrompt;
    const setTextAreaHeight = props.setTextAreaHeight;
    const expanded = props.expanded;
    const setExpanded = props.setExpanded;
    const smallChatHover = props.smallChatHover;
    const setSmallChatHover = props.setSmallChatHover;
    const smallChatHoverText = props.smallChatHoverText;
    const innerWidth = props.innerWidth;
    const redfinData = props.redfinData;
    const redfinMarketTrends = props.redfinMarketTrends;
    const floodZoneAnalysis = props.floodZoneAnalysis;
    const constructionMaterialAnalysis = props.constructionMaterialAnalysis;
    const fullSubdivisionAnalysis = props.fullSubdivisionAnalysis;
    const subjectPropertyAnalysis = props.subjectPropertyAnalysis;
    const basementAnalysis = props.basementAnalysis;
    const analysisArrivalOrder = useMemo(() => props.analysisArrivalOrder || {}, [props.analysisArrivalOrder]);
    const ceilings = props.ceilings;
    const viewComp = props.viewComp;
    const allComps = props.allComps;
    const arvModeActive = props.arvModeActive;
    const arvEstimate = props.arvEstimate;
    const arvRange = props.arvRange;
    const riskSpectrum = props.riskSpectrum;
    const focusChatbotInput = props.focusChatbotInput;
    const subdivisions = props.subdivisions;
    const tempStatus = props.tempStatus;
    const clickedZPID = props.clickedZPID;
    const [clickExpanded, setClickExpanded] = useState(null);
    const [selectedAddressZpids, setSelectedAddressZpids] = useState(new Set());
    const [highlightedZpid, setHighlightedZpid] = useState(null);
    const chatContainerRef = useRef(null);
    const inputContainerRef = useRef(null);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionStartPos, setMentionStartPos] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);
    const [filteredMentionComps, setFilteredMentionComps] = useState([]);
    const editableRef = useRef(null);
    const timeoutRefs = useRef([]); // Store all timeout IDs for cleanup
    const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] = useState(0);
    const [currentLoadingMessage, setCurrentLoadingMessage] = useState("");
    const loadingMessageKeyRef = useRef(0); // Force Typewriter to restart on message change
    const previousPendingCountRef = useRef(0); // Track previous pending messages count

    // Cleanup all timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(timeoutId => {
                if (timeoutId) clearTimeout(timeoutId);
            });
            timeoutRefs.current = [];
        };
    }, []);

    // Memoize event handlers to prevent unnecessary re-renders
    const openPanel = useCallback(() => {
        setExpanded(true);
        setSmallChatHover(false);
    }, [setExpanded, setSmallChatHover]);

    const closePanel = useCallback(() => {
        setExpanded(false);
    }, [setExpanded]);

    const textFinished = useCallback(() => {
        setTypeOutText(false);
    }, [setTypeOutText]);

    useEffect(() => {
        if ( arvModeActive === false ) return;
        const containerElement = chatContainerRef.current;
        if (!containerElement) return;
        // Delegated click handling: calls viewComp with the matching comp object
        const clickHandler = (e) => {
            const el = e.target.closest?.('.zpid-address');
            if (!el) return;
            e.preventDefault();
            const zpid = el.getAttribute('data-zpid');
            if (!zpid || !allComps || !viewComp) return;
            
            // Toggle selection state
            setSelectedAddressZpids(prev => {
                const newSet = new Set(prev);
                if (newSet.has(zpid)) {
                    newSet.delete(zpid);
                } else {
                    newSet.add(zpid);
                }
                return newSet;
            });
            
            const idx = allComps.findIndex(c => String(c?.uniqueIds?.zillowId) === String(zpid));
            if (idx !== -1) {
                try { viewComp(allComps[idx]); } catch(_) {}
            }
        };
        containerElement.addEventListener('click', clickHandler);
        return () => {
            if (containerElement) {
                containerElement.removeEventListener('click', clickHandler);
            }
        };
    }, [allComps, viewComp, arvModeActive]);

    // Apply bold styling to selected addresses
    useEffect(() => {
        if (arvModeActive === false) return;
        const containerElement = chatContainerRef.current;
        if (!containerElement) return;

        // Find all zpid-address elements and update their styling
        const addressElements = containerElement.querySelectorAll('.zpid-address');
        addressElements.forEach(el => {
            const zpid = el.getAttribute('data-zpid');
            if (selectedAddressZpids.has(zpid)) {
                el.classList.add('zpid-address-selected');
            } else {
                el.classList.remove('zpid-address-selected');
            }
        });
    }, [selectedAddressZpids, arvModeActive, arvMessages]);

    // Apply highlight to adjustment elements
    useEffect(() => {
        if (arvModeActive === false) return;
        const containerElement = chatContainerRef.current;
        if (!containerElement) return;

        // Find all adjustment elements and update their highlighting
        const adjustmentElements = containerElement.querySelectorAll('.small-chat-adjustments-element');
        adjustmentElements.forEach(el => {
            const zpid = el.getAttribute('data-zpid');
            if (highlightedZpid && String(highlightedZpid) === zpid) {
                el.classList.add('adjustment-element-highlight');
            } else {
                el.classList.remove('adjustment-element-highlight');
            }
        });
    }, [highlightedZpid, arvModeActive, arvMessages]);

    // Memoize lastMessage function - called in render loop for every message
    const lastMessage = useCallback((index) => {
        // Guard: need messages
        if (!Array.isArray(arvMessages) || arvMessages.length === 0) return false;

        // Find the last index where role === 'assistant'
        let lastAssistantIndex = undefined;
        for (let i = arvMessages.length - 1; i >= 0; i--) {
            if (arvMessages[i] && arvMessages[i].role === 'assistant') {
                lastAssistantIndex = i;
                break;
            }
        }

        // If no assistant message, or this isn't that message, return false
        if (lastAssistantIndex === undefined || index !== lastAssistantIndex) return false;

        // Check everything AFTER the last assistant message for disqualifying roles
        for (let j = lastAssistantIndex + 1; j < arvMessages.length; j++) {
            const role = arvMessages[j] && arvMessages[j].role;
            if (role === 'user_chat' || role === 'adjustments') {
                return false;
            }
        }

        // It's the last assistant message and nothing after it is user_chat or adjustments
        return true;
    }, [arvMessages]);

   const formatCompAddress = useCallback((comp) => {
       return comp.addressDetails.streetAddress;
   }, []);

   const handleMentionInput = (value, cursorPos) => {
       if (!allComps || allComps.length === 0) {
           setShowMentionDropdown(false);
           return;
       }

       // Find the last @ symbol before the cursor
       const textBeforeCursor = value.substring(0, cursorPos);
       const lastAtIndex = textBeforeCursor.lastIndexOf('@');
       
       if (lastAtIndex === -1) {
           setShowMentionDropdown(false);
           return;
       }

       // Check if there's a space after @, which means the mention is complete
       const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
       const hasSpaceAfter = textAfterAt.includes(' ') || textAfterAt.includes('\n');
       
       if (hasSpaceAfter) {
           setShowMentionDropdown(false);
           return;
       }

       // Check if this is a double @@ (two @ symbols in a row with no space between)
       // Only ignore if the @ we found is immediately preceded by another @
       // AND there's no space between them
       if (lastAtIndex > 0 && textBeforeCursor[lastAtIndex - 1] === '@') {
           setShowMentionDropdown(false);
           return;
       }

       // Extract the query after @
       setMentionStartPos(lastAtIndex);
       const query = textAfterAt.trim().toLowerCase();

       // Filter comps based on query (show all if query is empty)
       const filtered = allComps
           .map(comp => ({
               comp: comp,
               address: formatCompAddress(comp),
               label: formatCompAddress(comp)
           }))
           .filter(item => {
               if (query === "") {
                   // Show all comps when query is empty (just "@")
                   return true;
               }
               const addressLower = item.address.toLowerCase();
               return addressLower.includes(query);
           })
           .slice(0, 10); // Limit to 10 results

       if (filtered.length > 0) {
           setFilteredMentionComps(filtered);
           setShowMentionDropdown(true);
           setSelectedMentionIndex(-1);
       } else {
           setShowMentionDropdown(false);
       }
   };


   // Get plain text from contentEditable
   const getPlainText = (element) => {
       if (!element) return "";
       // Extract text from all nodes, replacing badges with addresses
       let text = "";
       const walker = document.createTreeWalker(
           element,
           NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
           {
               acceptNode: (node) => {
                   // Skip text nodes that are inside badge elements (they're already handled by the badge element itself)
                   if (node.nodeType === Node.TEXT_NODE) {
                       const parent = node.parentElement;
                       if (parent && parent.classList && parent.classList.contains('mention-badge-inline')) {
                           return NodeFilter.FILTER_REJECT;
                       }
                       // Also skip if parent's parent is a badge
                       if (parent && parent.parentElement && parent.parentElement.classList && parent.parentElement.classList.contains('mention-badge-inline')) {
                           return NodeFilter.FILTER_REJECT;
                       }
                   }
                   return NodeFilter.FILTER_ACCEPT;
               }
           }
       );
       
       let node;
       while ((node = walker.nextNode())) {
           if (node.nodeType === Node.TEXT_NODE) {
               text += node.textContent;
           } else if (node.classList && node.classList.contains('mention-badge-inline')) {
               // Extract address from badge - use data-address attribute or textContent
               const badgeAddress = node.getAttribute('data-address') || node.textContent.trim();
               text += badgeAddress + " ";
           }
       }
       return text.trim();
   };

   // Helper function to create a badge element with click handler
   const createBadgeElement = useCallback((address, fullAddress, comp) => {
       const badgeSpan = document.createElement('span');
       badgeSpan.className = 'mention-badge-inline';
       badgeSpan.contentEditable = 'false';
       badgeSpan.setAttribute('data-address', address);
       badgeSpan.setAttribute('data-full-address', fullAddress);
       badgeSpan.setAttribute('title', fullAddress);
       badgeSpan.style.cursor = 'pointer';
       
       // Add click and mousedown handlers to view the comp
       // Use mousedown to ensure it fires before contentEditable captures the event
       const handleMouseDown = (e) => {
           e.preventDefault();
           e.stopPropagation();
           if (viewComp && comp) {
               // Use setTimeout to ensure it fires after any contentEditable handlers
               const timeoutId = setTimeout(() => {
                   viewComp(comp);
               }, 0);
               timeoutRefs.current.push(timeoutId);
           }
       };
       
       badgeSpan.addEventListener('mousedown', handleMouseDown);
       
       // Also handle click as backup
       const handleClick = (e) => {
           e.preventDefault();
           e.stopPropagation();
           if (viewComp && comp) {
               viewComp(comp);
           }
       };
       
       badgeSpan.addEventListener('click', handleClick);
       
       const badgeComponent = document.createElement('span');
       badgeComponent.className = 'mention-badge-content';
       badgeComponent.textContent = address;
       badgeSpan.appendChild(badgeComponent);
       
       return badgeSpan;
   }, [viewComp]);

   // Render contentEditable with badges inline
   const renderEditableContent = (text) => {
       if (!allComps || allComps.length === 0) return text;
       
       // Split text by addresses and create segments
       const segments = [];
       let lastIndex = 0;
       const sortedAddresses = allComps
           .map(comp => ({
               address: comp.addressDetails.streetAddress,
               comp: comp,
               fullAddress: `${comp.addressDetails.streetAddress}, ${comp.addressDetails.city}, ${comp.addressDetails.state} ${comp.addressDetails.postalCode}`
           }))
           .sort((a, b) => b.address.length - a.address.length); // Sort by length to match longer addresses first
       
       // Find all address matches (can have multiple instances of same or different addresses)
       const matches = [];
       sortedAddresses.forEach(({ address, comp, fullAddress }) => {
           const escapedAddress = address.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
           const regex = new RegExp(`(?:^|\\s)(${escapedAddress})(?:$|\\s|,|\\.)`, 'gi');
           let match;
           const lastIndexes = [];
           while ((match = regex.exec(text)) !== null) {
               // Avoid infinite loops with zero-length matches
               if (match.index === lastIndexes[lastIndexes.length - 1]) break;
               lastIndexes.push(match.index);
               
               matches.push({
                   start: match.index + (match[0].startsWith(' ') ? 1 : 0),
                   end: match.index + match[0].length - (match[0].endsWith(' ') || match[0].endsWith(',') || match[0].endsWith('.') ? 1 : 0),
                   address: match[1],
                   comp: comp,
                   fullAddress: fullAddress
               });
           }
       });
       
       // Sort matches by position
       matches.sort((a, b) => a.start - b.start);
       
       // Remove overlapping matches (keep first)
       const nonOverlapping = [];
       matches.forEach(match => {
           if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
               nonOverlapping.push(match);
           }
       });
       
       // Build segments
       nonOverlapping.forEach(match => {
           if (match.start > lastIndex) {
               segments.push({ type: 'text', content: text.substring(lastIndex, match.start) });
           }
           segments.push({ type: 'badge', address: match.address, fullAddress: match.fullAddress, comp: match.comp });
           lastIndex = match.end;
       });
       
       if (lastIndex < text.length) {
           segments.push({ type: 'text', content: text.substring(lastIndex) });
       }
       
       return segments.length > 0 ? segments : [{ type: 'text', content: text }];
   };

   const handleEditableInput = (e) => {
       const element = e.target;
       const plainText = getPlainText(element);
       
       // Check for @ mentions
       const selection = window.getSelection();
       const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
       
       // Calculate cursor position in plain text
       let cursorPos = plainText.length;
       if (range) {
           const rangeClone = range.cloneRange();
           rangeClone.setStart(editableRef.current, 0);
           cursorPos = rangeClone.toString().length;
       }
       
       handleMentionInput(plainText, cursorPos);
       
       // Update parent component
       onChangePrompt({ target: { value: plainText } });
       
       // Auto-adjust height
       if (element.style) {
           element.style.height = "auto";
           element.style.height = element.scrollHeight + "px";
           setTextAreaHeight(element.scrollHeight);
       }
       
       // Convert addresses to badges after a short delay
       const timeoutId = setTimeout(() => {
               const currentText = getPlainText(editableRef.current);
               if (currentText === plainText && allComps && allComps.length > 0) {
                   const segments = renderEditableContent(plainText);
                   const shouldHaveBadges = segments.some(s => s.type === 'badge');
                   const existingBadgeCount = editableRef.current.querySelectorAll('.mention-badge-inline').length;
                   const expectedBadgeCount = segments.filter(s => s.type === 'badge').length;
                   
                   // Always rebuild with badges if addresses are detected (supports multiple badges)
                   // Rebuild if we should have badges and either have none or the count doesn't match
                   if (shouldHaveBadges && existingBadgeCount !== expectedBadgeCount) {
                       // Save cursor
                       const sel = window.getSelection();
                       let cursorOffset = 0;
                       if (sel.rangeCount > 0) {
                           const r = sel.getRangeAt(0).cloneRange();
                           r.setStart(editableRef.current, 0);
                           cursorOffset = r.toString().length;
                       }
                   
                       // Rebuild with badges
                       editableRef.current.innerHTML = "";
                       segments.forEach(segment => {
                           if (segment.type === 'text') {
                               editableRef.current.appendChild(document.createTextNode(segment.content));
                           } else if (segment.type === 'badge') {
                               const badgeSpan = createBadgeElement(segment.address, segment.fullAddress, segment.comp);
                               editableRef.current.appendChild(badgeSpan);
                               editableRef.current.appendChild(document.createTextNode(" "));
                           }
                       });
                       
                       // Restore cursor
                       const restoreTimeoutId = setTimeout(() => {
                           try {
                               const r = document.createRange();
                               const walker = document.createTreeWalker(
                                   editableRef.current,
                                   NodeFilter.SHOW_TEXT,
                                   null
                               );
                               let offset = 0;
                               let targetNode = null;
                               
                               let node;
                               while ((node = walker.nextNode())) {
                                   if (offset + node.textContent.length >= cursorOffset) {
                                       targetNode = node;
                                       break;
                                   }
                                   offset += node.textContent.length;
                               }
                               
                               if (targetNode) {
                                   r.setStart(targetNode, Math.min(cursorOffset - offset, targetNode.textContent.length));
                                   r.collapse(true);
                                   sel.removeAllRanges();
                                   sel.addRange(r);
                               }
                           } catch (e) {
                               // Ignore cursor errors
                           }
                           
                           // Re-check for mentions after cursor is restored
                           const mentionTimeoutId = setTimeout(() => {
                               const selectionAfterRebuild = window.getSelection();
                               const rangeAfterRebuild = selectionAfterRebuild.rangeCount > 0 ? selectionAfterRebuild.getRangeAt(0) : null;
                               let cursorPosAfterRebuild = currentText.length;
                               if (rangeAfterRebuild) {
                                   const rangeClone = rangeAfterRebuild.cloneRange();
                                   rangeClone.setStart(editableRef.current, 0);
                                   cursorPosAfterRebuild = rangeClone.toString().length;
                               }
                               handleMentionInput(currentText, cursorPosAfterRebuild);
                           }, 20);
                           timeoutRefs.current.push(mentionTimeoutId);
                       }, 0);
                       timeoutRefs.current.push(restoreTimeoutId);
                   } else if (!shouldHaveBadges) {
                       // If we shouldn't have badges but there's an @, check for mentions
                       const selection = window.getSelection();
                       const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
                       let cursorPos = currentText.length;
                       if (range) {
                           const rangeClone = range.cloneRange();
                           rangeClone.setStart(editableRef.current, 0);
                           cursorPos = rangeClone.toString().length;
                       }
                       handleMentionInput(currentText, cursorPos);
                   }
           }
       }, 100);
       timeoutRefs.current.push(timeoutId);
   };

   const handleEditableKeyDown = (e) => {
       // Handle mention dropdown navigation
       handleMentionKeyDown(e);
       
       // Handle backspace to delete entire badge
       if (e.key === "Backspace") {
           const selection = window.getSelection();
           if (selection.rangeCount > 0) {
               const range = selection.getRangeAt(0);
               const node = range.startContainer;
               
               // Helper function to find badge before cursor - more comprehensive
               const findBadgeBeforeCursor = () => {
                   // Case 1: Cursor is inside a badge element
                   if (node.nodeType === Node.ELEMENT_NODE) {
                       const badgeElement = node.classList && node.classList.contains('mention-badge-inline')
                           ? node
                           : node.closest && node.closest('.mention-badge-inline');
                       if (badgeElement) {
                           return badgeElement;
                       }
                   }
                   
                   // Case 2: Cursor is at the start of a text node (right after badge + space)
                   if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
                       let currentSibling = node.previousSibling;
                       while (currentSibling) {
                           if (currentSibling.classList && currentSibling.classList.contains('mention-badge-inline')) {
                               return currentSibling;
                           }
                           // Skip empty/space text nodes
                           if (currentSibling.nodeType === Node.TEXT_NODE) {
                               const text = currentSibling.textContent.trim();
                               if (text === "" || text === " ") {
                                   currentSibling = currentSibling.previousSibling;
                                   continue;
                               }
                           }
                           break;
                       }
                   }
                   
                   // Case 3: Cursor is at the end of a space text node (right after badge)
                   if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === "") {
                       const textContent = node.textContent;
                       // If cursor is at the end of a space-only node
                       if (range.startOffset === textContent.length || range.startOffset === textContent.length - 1) {
                           let currentSibling = node.previousSibling;
                           if (currentSibling && currentSibling.classList && currentSibling.classList.contains('mention-badge-inline')) {
                               return currentSibling;
                           }
                       }
                   }
                   
                   // Case 4: Cursor is at start of element, check previous sibling
                   if (node.nodeType === Node.ELEMENT_NODE && range.startOffset === 0) {
                       let currentSibling = node.previousSibling;
                       while (currentSibling) {
                           if (currentSibling.classList && currentSibling.classList.contains('mention-badge-inline')) {
                               return currentSibling;
                           }
                           if (currentSibling.nodeType === Node.TEXT_NODE && currentSibling.textContent.trim() === "") {
                               currentSibling = currentSibling.previousSibling;
                           } else {
                               break;
                           }
                       }
                   }
                   
                   return null;
               };
               
               const badgeElement = findBadgeBeforeCursor();
               if (badgeElement) {
                   e.preventDefault();
                   e.stopPropagation();
                   
                   // Get the badge address
                   const badgeAddress = badgeElement.getAttribute('data-address') || badgeElement.textContent.trim();
                   
                   // Find where the badge is in the DOM to calculate cursor position
                   // Walk through all nodes to find badge position
                   const walker = document.createTreeWalker(
                       editableRef.current,
                       NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
                       null
                   );
                   let offset = 0;
                   let badgeOffset = -1;
                   let node;
                   while ((node = walker.nextNode())) {
                       if (node === badgeElement) {
                           badgeOffset = offset;
                           break;
                       }
                       if (node.nodeType === Node.TEXT_NODE) {
                           offset += node.textContent.length;
                       } else if (node.classList && node.classList.contains('mention-badge-inline')) {
                           // This is a badge, add its address length
                           const addr = node.getAttribute('data-address') || node.textContent.trim();
                           offset += addr.length + 1; // +1 for space
                       }
                   }
                   
                   // Calculate cursor position - it should be at the start of where badge was
                   const targetOffset = badgeOffset >= 0 ? badgeOffset : Math.max(0, offset - badgeAddress.length - 1);
                   
                   // Remove badge and any trailing space
                   const nextSibling = badgeElement.nextSibling;
                   badgeElement.remove();
                   if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent.trim() === "") {
                       nextSibling.remove();
                   }
                   
                   // Get plain text after deletion
                   const plainText = getPlainText(editableRef.current);
                   
                   // Restore cursor immediately BEFORE calling onChangePrompt
                   try {
                       const newRange = document.createRange();
                       const textWalker = document.createTreeWalker(
                           editableRef.current,
                           NodeFilter.SHOW_TEXT,
                           null
                       );
                       let textOffset = 0;
                       let targetNode = null;
                       let textNode;
                       while ((textNode = textWalker.nextNode())) {
                           if (textOffset + textNode.textContent.length >= targetOffset) {
                               targetNode = textNode;
                               break;
                           }
                           textOffset += textNode.textContent.length;
                       }
                       
                       if (targetNode) {
                           newRange.setStart(targetNode, Math.min(targetOffset - textOffset, targetNode.textContent.length));
                           newRange.collapse(true);
                           selection.removeAllRanges();
                           selection.addRange(newRange);
                       }
                   } catch (err) {
                       // Continue even if cursor restore fails
                   }
                   
                   // Now update the state - cursor should already be in correct position
                   onChangePrompt({ target: { value: plainText } });
                   
                   // Also restore cursor after a brief delay to handle any rebuilds
                   const restoreTimeoutId = setTimeout(() => {
                       try {
                           const newRange = document.createRange();
                           const finalWalker = document.createTreeWalker(
                               editableRef.current,
                               NodeFilter.SHOW_TEXT,
                               null
                           );
                           let finalOffset = 0;
                           let finalTargetNode = null;
                           let finalNode;
                           while ((finalNode = finalWalker.nextNode())) {
                               if (finalOffset + finalNode.textContent.length >= targetOffset) {
                                   finalTargetNode = finalNode;
                                   break;
                               }
                               finalOffset += finalNode.textContent.length;
                           }
                           
                           if (finalTargetNode) {
                               newRange.setStart(finalTargetNode, Math.min(targetOffset - finalOffset, finalTargetNode.textContent.length));
                               newRange.collapse(true);
                               const sel = window.getSelection();
                               sel.removeAllRanges();
                               sel.addRange(newRange);
                           }
                       } catch (err) {
                           // Ignore errors
                       }
                   }, 20);
                   timeoutRefs.current.push(restoreTimeoutId);
                   
                   return;
               }
           }
       }
       
       // Handle Enter for submit
       if (e.key === "Enter" && !e.shiftKey && !e.defaultPrevented) {
           const plainText = getPlainText(editableRef.current);
           if (plainText.trim() !== "") {
               e.preventDefault();
               submitPrompt();
           }
       }
       
   };

   const insertMention = (selectedComp) => {
       if (!editableRef.current) return;
       
       const address = formatCompAddress(selectedComp.comp);
       const plainText = getPlainText(editableRef.current);
       const selection = window.getSelection();
       
       // Find @ symbol in plain text
       const atIndex = mentionStartPos !== -1 ? mentionStartPos : plainText.lastIndexOf("@");
       
       if (atIndex === -1) {
           // Fallback: just append
           const newText = plainText + (plainText ? " " : "") + address + " ";
           onChangePrompt({ target: { value: newText } });
           setShowMentionDropdown(false);
           setMentionStartPos(-1);
           setSelectedMentionIndex(0);
           return;
       }
       
       // Replace @... with address
       const beforeMention = plainText.substring(0, atIndex);
       const cursorPos = plainText.length; // Insert at end for now
       const afterMention = plainText.substring(cursorPos);
       const newPlainText = beforeMention + address + " " + afterMention;
       
       // Update the text
       onChangePrompt({ target: { value: newPlainText } });
       
       // Rebuild with badges
       const segments = renderEditableContent(newPlainText);
       editableRef.current.innerHTML = "";
       segments.forEach(segment => {
           if (segment.type === 'text') {
               editableRef.current.appendChild(document.createTextNode(segment.content));
           } else if (segment.type === 'badge') {
               const badgeSpan = createBadgeElement(segment.address, segment.fullAddress, segment.comp);
               editableRef.current.appendChild(badgeSpan);
               editableRef.current.appendChild(document.createTextNode(" "));
           }
       });
       
       // Set cursor at end
       const timeoutId = setTimeout(() => {
           const range = document.createRange();
           const walker = document.createTreeWalker(
               editableRef.current,
               NodeFilter.SHOW_TEXT,
               null
           );
           let lastNode = null;
           let node;
           while ((node = walker.nextNode())) {
               lastNode = node;
           }
           if (lastNode) {
               range.setStart(lastNode, lastNode.textContent.length);
               range.collapse(true);
               selection.removeAllRanges();
               selection.addRange(range);
           }
           editableRef.current.focus();
       }, 0);
       timeoutRefs.current.push(timeoutId);
       
       // Close dropdown and reset
       setShowMentionDropdown(false);
       setMentionStartPos(-1);
       setSelectedMentionIndex(-1);
   };

   // Initialize contentEditable with value
   useEffect(() => {
       if (!editableRef.current) return;
       
       const currentText = getPlainText(editableRef.current);
       
       // Only update if content is different to avoid infinite loops
       if (currentText !== arvDescriptionPrompt) {
           if (!arvDescriptionPrompt || arvDescriptionPrompt.trim() === "") {
               editableRef.current.textContent = "";
           } else {
               // Update with badges if there are addresses
               const segments = allComps && allComps.length > 0 
                   ? renderEditableContent(arvDescriptionPrompt)
                   : [{ type: 'text', content: arvDescriptionPrompt }];
               
               editableRef.current.innerHTML = "";
               segments.forEach(segment => {
                   if (segment.type === 'text') {
                       editableRef.current.appendChild(document.createTextNode(segment.content));
                   } else if (segment.type === 'badge') {
                       const badgeSpan = document.createElement('span');
                       badgeSpan.className = 'mention-badge-inline';
                       badgeSpan.contentEditable = 'false';
                       badgeSpan.setAttribute('data-address', segment.address);
                       badgeSpan.setAttribute('data-full-address', segment.fullAddress);
                       badgeSpan.setAttribute('title', segment.fullAddress);
                       
                       const badgeComponent = document.createElement('span');
                       badgeComponent.className = 'mention-badge-content';
                       badgeComponent.textContent = segment.address;
                       badgeSpan.appendChild(badgeComponent);
                       
                       editableRef.current.appendChild(badgeSpan);
                       editableRef.current.appendChild(document.createTextNode(" "));
                   }
               });
           }
       } else {
           // Even if text matches, check if we need to add/update badges (supports multiple badges)
           if (allComps && allComps.length > 0 && arvDescriptionPrompt) {
               const segments = renderEditableContent(arvDescriptionPrompt);
               const shouldHaveBadges = segments.some(s => s.type === 'badge');
               const existingBadgeCount = editableRef.current.querySelectorAll('.mention-badge-inline').length;
               const expectedBadgeCount = segments.filter(s => s.type === 'badge').length;
               const hasBadges = existingBadgeCount > 0;
               
               // Always rebuild if badges should be present (supports multiple badges and updates)
               // Rebuild if we should have badges and either have none or the count doesn't match
               if (shouldHaveBadges && existingBadgeCount !== expectedBadgeCount) {
                   // Save cursor position
                   const selection = window.getSelection();
                   let cursorOffset = 0;
                   if (selection.rangeCount > 0) {
                       const range = selection.getRangeAt(0).cloneRange();
                       range.setStart(editableRef.current, 0);
                       cursorOffset = range.toString().length;
                   }
                   
                   // Rebuild with badges
                   editableRef.current.innerHTML = "";
                   segments.forEach(segment => {
                       if (segment.type === 'text') {
                           editableRef.current.appendChild(document.createTextNode(segment.content));
                       } else if (segment.type === 'badge') {
                           const badgeSpan = createBadgeElement(segment.address, segment.fullAddress, segment.comp);
                           editableRef.current.appendChild(badgeSpan);
                           editableRef.current.appendChild(document.createTextNode(" "));
                       }
                   });
                   
                   // Restore cursor position (approximate)
                   const restoreTimeoutId = setTimeout(() => {
                       try {
                           const range = document.createRange();
                           const walker = document.createTreeWalker(
                               editableRef.current,
                               NodeFilter.SHOW_TEXT,
                               null
                           );
                           let currentOffset = 0;
                           let targetNode = null;
                           
                           let node;
                           while ((node = walker.nextNode())) {
                               if (currentOffset + node.textContent.length >= cursorOffset) {
                                   targetNode = node;
                                   break;
                               }
                               currentOffset += node.textContent.length;
                           }
                           
                           if (targetNode) {
                               range.setStart(targetNode, Math.min(cursorOffset - currentOffset, targetNode.textContent.length));
                               range.collapse(true);
                               selection.removeAllRanges();
                               selection.addRange(range);
                           }
                       } catch (e) {
                           // Ignore cursor restoration errors
                       }
                   }, 0);
                   timeoutRefs.current.push(restoreTimeoutId);
               } else if (!shouldHaveBadges && hasBadges && currentText === "") {
                   // Clear badges if text is empty
                   editableRef.current.innerHTML = "";
               }
           }
       }
       // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [arvDescriptionPrompt, allComps]);


   const handleMentionKeyDown = (e) => {
       if (!showMentionDropdown || filteredMentionComps.length === 0) return;

       if (e.key === "ArrowDown") {
           e.preventDefault();
           setSelectedMentionIndex(prev => {
               if (prev === -1) return 0; // Start at first item if nothing selected
               return prev < filteredMentionComps.length - 1 ? prev + 1 : prev;
           });
       } else if (e.key === "ArrowUp") {
           e.preventDefault();
           setSelectedMentionIndex(prev => {
               if (prev === -1) return filteredMentionComps.length - 1; // Start at last item if nothing selected
               return prev > 0 ? prev - 1 : prev;
           });
       } else if (e.key === "Enter" && !e.shiftKey) {
           e.preventDefault();
           // If nothing is selected, select the first item
           const indexToUse = selectedMentionIndex === -1 ? 0 : selectedMentionIndex;
           if (filteredMentionComps[indexToUse]) {
               insertMention(filteredMentionComps[indexToUse]);
           }
       } else if (e.key === "Escape") {
           e.preventDefault();
           setShowMentionDropdown(false);
       }
   };

   // Reset selected index when filtered comps change
   useEffect(() => {
       if (filteredMentionComps.length > 0 && selectedMentionIndex >= filteredMentionComps.length) {
           setSelectedMentionIndex(-1);
       }
   }, [filteredMentionComps, selectedMentionIndex]);

   // Scroll selected item into view
   useEffect(() => {
       if (showMentionDropdown && inputContainerRef.current) {
           const container = inputContainerRef.current.querySelector('.predictive-search-container');
           const selectedElement = container?.querySelector('.predictive-search-selected');
           if (selectedElement && container) {
               selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
           }
       }
   }, [selectedMentionIndex, showMentionDropdown]);

   // Handle focus
   useEffect(() => {
       if (focusChatbotInput && editableRef.current) {
           editableRef.current.focus();
       }
   }, [focusChatbotInput]);

   // Handle paste to strip formatting
   useEffect(() => {
       const element = editableRef.current;
       if (!element) return;
       
       const handlePaste = (e) => {
           e.preventDefault();
           const text = (e.clipboardData || window.clipboardData).getData('text');
           document.execCommand('insertText', false, text);
       };
       
       element.addEventListener('paste', handlePaste);
       return () => {
           if (element) {
               element.removeEventListener('paste', handlePaste);
           }
       };
   }, []);

   // Close dropdown when clicking outside
   useEffect(() => {
       const handleClickOutside = (event) => {
           if (inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
               setShowMentionDropdown(false);
           }
       };

       if (showMentionDropdown) {
           document.addEventListener('mousedown', handleClickOutside);
           return () => {
               document.removeEventListener('mousedown', handleClickOutside);
           };
       }
   }, [showMentionDropdown]);

   // Generate and rotate loading messages for pending analyses
   useEffect(() => {
       if (arvEstimate != null) {
           // Don't show loading messages if ARV estimate exists
           setCurrentLoadingMessage("");
           setCurrentLoadingMessageIndex(0);
           return;
       }

       // Define all possible analysis messages
       const allMessages = [
           { key: 'subjectProperty', text: 'Analyzing subject property details...' },
           { key: 'floodZone', text: 'Checking flood zone status...' },
           { key: 'redfin', text: 'Analyzing market trends...' },
           { key: 'construction', text: 'Identifying construction materials...' },
           { key: 'subdivision', text: 'Analyzing subdivision data...' },
           { key: 'basement', text: 'Checking basement status...' },
           { key: 'priceCeiling', text: 'Checking for price ceilings...' }
       ];

       // Filter out messages for analyses that have already completed
       const pendingMessages = allMessages.filter(msg => {
           switch (msg.key) {
               case 'subjectProperty':
                   return !subjectPropertyAnalysis;
               case 'floodZone':
                   return !floodZoneAnalysis;
               case 'redfin':
                   return !redfinMarketTrends;
               case 'construction':
                   return !constructionMaterialAnalysis;
               case 'subdivision':
                   return !(fullSubdivisionAnalysis && fullSubdivisionAnalysis.results && fullSubdivisionAnalysis.results.length > 0);
               case 'basement':
                   return !basementAnalysis;
               case 'priceCeiling':
                   return !(ceilings && ceilings.length > 0);
               default:
                   return true;
           }
       });

       // If no pending messages, show a generic message
       if (pendingMessages.length === 0) {
           setCurrentLoadingMessage('Preparing analysis...');
           loadingMessageKeyRef.current += 1;
           previousPendingCountRef.current = 0;
           return;
       }

       // Reset index if pending messages count changed
       if (previousPendingCountRef.current !== pendingMessages.length) {
           setCurrentLoadingMessageIndex(0);
           previousPendingCountRef.current = pendingMessages.length;
       }

       // Reset index if it's out of bounds
       const safeIndex = currentLoadingMessageIndex % pendingMessages.length;
       const newMessage = pendingMessages[safeIndex].text;
       
       setCurrentLoadingMessage(newMessage);
       loadingMessageKeyRef.current += 1;

       // Rotate to next message after 3 seconds
       const rotateTimeout = setTimeout(() => {
           setCurrentLoadingMessageIndex(prev => (prev + 1) % pendingMessages.length);
       }, 3000);

       timeoutRefs.current.push(rotateTimeout);

       return () => {
           if (rotateTimeout) clearTimeout(rotateTimeout);
       };
   }, [arvEstimate, subjectPropertyAnalysis, floodZoneAnalysis, redfinMarketTrends, constructionMaterialAnalysis, fullSubdivisionAnalysis, basementAnalysis, ceilings, currentLoadingMessageIndex]);

    // Memoize analysis components to prevent infinite re-renders
    const memoizedAnalysisComponents = useMemo(() => {
        // Build array of analysis components with their data and arrival times
        const analysisComponents = [];
        
        if (subjectPropertyAnalysis && analysisArrivalOrder.subjectProperty) {
            analysisComponents.push({
                type: 'subjectProperty',
                arrivalTime: analysisArrivalOrder.subjectProperty.arrivalTime,
                component: (
                    <div key="subjectProperty" className="analysis-component-fade-in">
                        <SubjectPropertyAnalysis
                            subjectPropertyAnalysis={subjectPropertyAnalysis}
                            darkMode={false}
                            showScrollableImages={props.showScrollableImages}
                        />
                    </div>
                )
            });
        }

        if (fullSubdivisionAnalysis && fullSubdivisionAnalysis.results && fullSubdivisionAnalysis.results.length > 0 && analysisArrivalOrder.subdivision) {
            analysisComponents.push({
                type: 'subdivision',
                arrivalTime: analysisArrivalOrder.subdivision.arrivalTime,
                component: (
                    <div key="subdivision" className="analysis-component-fade-in">
                        <div className="light-grey-divider" />
                        <SubdivisionAnalysis
                            fullSubdivisionAnalysis={fullSubdivisionAnalysis}
                            darkMode={false}
                            allComps={allComps}
                            viewComp={viewComp}
                        />
                    </div>
                )
            });
        }
        
        if (floodZoneAnalysis && analysisArrivalOrder.floodZone) {
            analysisComponents.push({
                type: 'floodZone',
                arrivalTime: analysisArrivalOrder.floodZone.arrivalTime,
                component: (
                    <div key="floodZone" className="analysis-component-fade-in">
                        <div className="light-grey-divider" />
                        <FloodZoneAnalysis
                            floodZoneAnalysis={floodZoneAnalysis}
                            darkMode={false}
                        />
                    </div>
                )
            });
        }
        
        if (redfinMarketTrends && analysisArrivalOrder.redfin) {
            analysisComponents.push({
                type: 'redfin',
                arrivalTime: analysisArrivalOrder.redfin.arrivalTime,
                component: (
                    <div key="redfin" className="analysis-component-fade-in">
                        <div className="light-grey-divider" />
                        <RedfinMarketTrends
                            redfinMarketTrends={redfinMarketTrends}
                            darkMode={false}
                        />
                    </div>
                )
            });
        }
        
        if (constructionMaterialAnalysis && analysisArrivalOrder.construction) {
            analysisComponents.push({
                type: 'construction',
                arrivalTime: analysisArrivalOrder.construction.arrivalTime,
                component: (
                    <div key="construction" className="analysis-component-fade-in">
                        <div className="light-grey-divider" />
                        <ConstructionMaterialAnalysis
                            constructionMaterialAnalysis={constructionMaterialAnalysis}
                            darkMode={false}
                        />
                    </div>
                )
            });
        }
        
        if (basementAnalysis && analysisArrivalOrder.basement) {
            analysisComponents.push({
                type: 'basement',
                arrivalTime: analysisArrivalOrder.basement.arrivalTime,
                component: (
                    <div key="basement" className="analysis-component-fade-in">
                        <div className="light-grey-divider" />
                        <BasementAnalysis
                            basementAnalysis={basementAnalysis}
                            darkMode={false}
                        />
                    </div>
                )
            });
        }
        
        // Sort with priority: SubjectProperty first, Subdivision second, then by arrival time
        analysisComponents.sort((a, b) => {
            // SubjectProperty always comes first
            if (a.type === 'subjectProperty') return -1;
            if (b.type === 'subjectProperty') return 1;
            
            // Subdivision always comes second
            if (a.type === 'subdivision') return -1;
            if (b.type === 'subdivision') return 1;
            
            // For all other components, sort by arrival time
            return a.arrivalTime - b.arrivalTime;
        });
        
        // Render sorted components
        return analysisComponents.map(item => item.component);
    }, [
        subjectPropertyAnalysis,
        floodZoneAnalysis,
        redfinMarketTrends,
        constructionMaterialAnalysis,
        fullSubdivisionAnalysis,
        basementAnalysis,
        analysisArrivalOrder,
        props.showScrollableImages,
        allComps,
        viewComp
    ]);

    return (
        <div className={"small-chat-outer-container " + (collapsed === true ? "small-chat-collapsed" : "small-chat-expanded")}>
            {
                clickExpanded !== null &&
                <ExitScreen
                    clickScreen={() => setClickExpanded(null)}
                />
            }
            {
                expanded === false && innerWidth < 1280 ?
                <ChatIcon
                    hoverEffect={true}
                    toggleClick={() => openPanel()}
                    expanded={smallChatHover}
                    setExpanded={setSmallChatHover}
                    hoverText={smallChatHoverText}
                />
                :
                <div 
                    className={"small-chat-inner-container relative-container " + (arvEstimate === null ? "small-chat-no-arv" : "")}
                    style={{ '--text-area-height': `${textAreaHeight}px` }}
                >
                    {
                        innerWidth < 1280 &&
                        <div className="small-chat-header-icons-row">
                            <IconButton
                                size="medium"
                                icon={<Exit fill={colour.gray07} size={16} className="modal-exit-icon" clickFunc={null} />}
                                disabled={false}
                                clickFunc={() => closePanel()}
                                standardIcon={false}
                                background={true}
                                bgType={"white"}
                                contextTooltip={false}
                                contextTooltipBG={null}
                                contextTooltipText={null}
                                customClass={null}
                            />
                        </div>
                    }
                    <div className={"small-chat-description-container " + (readOnly === true ? "small-chat-description-container-read-only" : "")}>
                        <div className="small-chat-text-container" ref={chatContainerRef}>
                            {memoizedAnalysisComponents}
                            {
                                ceilings && ceilings.length > 0 && allComps && allComps.length > 0 &&
                                <>
                                    <div className="light-grey-divider" />
                                    <PriceCeilingShowcase
                                        ceilings={ceilings}
                                        allComps={allComps}
                                        darkMode={false}
                                    />
                                </>
                            }
                            {
                                arvMessages.map((item, index) =>
                                    item.role === "system" || item.role === "user" ?
                                    null
                                    :
                                    item.role === "redfin_analysis" && redfinData === null ?
                                    null
                                    :
                                    arvEstimate === null ?
                                    null
                                    :
                                    item.compPreferences === true ?
                                    null
                                    :
                                    item.role === "event" ?
                                    <div 
                                        className="small-chat-event-outer-element"
                                        key={index}
                                    >
                                        <div className="small-chat-event-element">
                                            <div className="small-chat-event-border">
                                            </div>
                                            {
                                                item.content.includes("Comps selection") ?
                                                <HugeiconsIcon icon={GuestHouseIcon} size={13} color={colour.gray05} />
                                                :
                                                item.content.includes("ARV Estimate Regenerated") || item.content.includes("strategy selected") ?
                                                <Wand fill={colour.gray05} className="small-chat-description-wand-icon" />
                                                :
                                                null
                                            }
                                            <span className="body-regular colour-secondary block-text text-align-center">
                                                {item.content}
                                            </span>
                                            <div className="small-chat-event-border">
                                            </div>
                                        </div>
                                        {
                                            index === arvMessages.length - 1 && item.content === "Comps selection changed." && loading === false && arvLoading === false &&
                                            <div 
                                                className="text-button"
                                                onClick={() => submitPrompt("Regenerate ARV based on my selected comps")}
                                            >
                                                <span className="body-regular colour-primary">
                                                    Regenerate ARV
                                                </span>
                                            </div>
                                        }
                                    </div>
                                    :
                                    item.role === "adjustments" ?
                                    <React.Fragment
                                        key={index}
                                    >
                                        <div className="small-chat-text-element small-chat-text-assistant-width">
                                            {/* OLD ADJUSTMENTS RENDERING - COMMENTED OUT
                                            {
                                                item.content === null ?
                                                null
                                                :
                                                typeOutText === true && (index === arvMessages.length - 1 || (arvMessages.length === 2 && index === 1)) && arvLoading === false && loading === false ?
                                                <Typewriter
                                                    text={item.content}
                                                    delay={10}
                                                    commaDelay={0}
                                                    fullStopDelay={0} 
                                                    play={true}
                                                    className="body-regular colour-primary block-text white-space-pre-wrap typewriter-text-gap"
                                                    isHTML={true}
                                                    onComplete={textFinished}
                                                    allComps={allComps}
                                                    clickable={true}
                                                    adjustments={true}
                                                    zipcode={redfinMarketTrends?.zipcode}
                                                />
                                                :
                                                <div
                                                    className={"body-regular colour-primary typewriter-text-gap"}
                                                    dangerouslySetInnerHTML={{ __html: renderHTMLAdjustments(item.content, allComps, { clickable: true, zipcode: redfinMarketTrends?.zipcode }) }}
                                                />
                                            }
                                            */}
                                            <div className="adjustments-content-container">
                                                {
                                                    item.content && Array.isArray(item.content) && item.content.length > 0 ?
                                                    <CompsDistribution
                                                        adjustmentsData={item.content}
                                                        allComps={allComps}
                                                        arvEstimate={arvEstimate}
                                                        arvRange={arvRange}
                                                        riskSpectrum={riskSpectrum}
                                                        viewComp={viewComp}
                                                        clickedZPID={clickedZPID}
                                                        onCompClick={(comp) => {
                                                            // Trigger highlight when comp is clicked
                                                            const zpid = comp?.zpid || comp?.uniqueIds?.zillowId;
                                                            if (zpid) {
                                                                setHighlightedZpid(String(zpid));
                                                                setTimeout(() => {
                                                                    setHighlightedZpid(null);
                                                                }, 1000);
                                                            }
                                                        }}
                                                    />
                                                    :
                                                    null
                                                }
                                                <span className="body-bold colour-primary">Comps</span>
                                                <div
                                                    className={"body-regular colour-primary adjustments-content-container-text"}
                                                    dangerouslySetInnerHTML={{ __html: renderHTMLAdjustments(item.content, allComps, { clickable: true, zipcode: redfinMarketTrends?.zipcode, clickedZPID: clickedZPID }) }}
                                                />
                                            </div>
                                        </div>
                                        {/* {
                                            showAdjustmentDialogue === true && arvLoading === false &&
                                            <Dialogue
                                                bodyText={innerWidth < 1399 && innerWidth >= 1280 ? null : "Using your custom adjustments"}
                                                linkText={innerWidth < 1399 && innerWidth >= 1280 ? "View or change adjustments" : "View or change"}
                                                linkCTA={"/settings"}
                                                buttonText={"Dismiss"}
                                                buttonCTA={closeAdjustmentDialogue}
                                                buttonDisabled={false}
                                                fixed={false}
                                                bgColour="bg-colour-pink-01"
                                                infoFill={colour.pink02}
                                            />
                                        } */}
                                    </React.Fragment>
                                    :
                                    <div 
                                        className={"small-chat-text-element " + (item.role === "user_chat" ? "small-chat-text-bubble" : "")}
                                        key={index}
                                    >
                                        {
                                            item.role === "assistant" &&
                                            <div className="small-chat-sparkle-container">
                                                <Sparkle
                                                    fill={colour.gray06}
                                                    className=""
                                                    gradient={false}
                                                />
                                            </div>
                                        }
                                        <div className={"small-chat-text-content-container " + (item.role === "assistant" ? "small-chat-text-assistant-width " : "") + (index < 3 ? "small-chat-no-bullets" : "")}>
                                            {
                                                item.content === null ?
                                                null
                                                :
                                                item.role === "assistant" && typeOutText === true && (lastMessage(index) === true) && arvLoading === false && loading === false ?
                                                <Typewriter
                                                    text={item.content}
                                                    delay={10}
                                                    commaDelay={0}
                                                    fullStopDelay={0} 
                                                    play={true}
                                                    className="body-regular colour-primary block-text white-space-pre-wrap typewriter-text-gap"
                                                    isHTML={true}
                                                    onComplete={textFinished}
                                                    allComps={allComps}
                                                    clickable={true}
                                                />
                                                :
                                                <div
                                                    className={"body-regular colour-primary typewriter-text-gap-small"}
                                                    dangerouslySetInnerHTML={{ __html: renderHTML(item.content, allComps, { clickable: true }) }}
                                                />
                                            }
                                            {
                                                item.compPreferences && subdivisions.length > 0 &&
                                                <SubdivisionAccordion
                                                    subdivisions={subdivisions}
                                                />
                                            }
                                        </div>
                                    </div>
                                )
                            }
                            {
                                (arvLoading === true || loading === true) &&
                                <div className="small-chat-text-element">
                                    <div className="small-chat-sparkle-container">
                                        <Sparkle
                                            fill={colour.gray06}
                                            className=""
                                            gradient={false}
                                        />
                                    </div>
                                    <div className="small-chat-text-content-container small-chat-text-assistant-width">
                                        <span className="body-regular colour-secondary">
                                            {tempStatus}
                                        </span>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    {
                        readOnly === false && arvEstimate != null &&
                        <div className="small-chat-input-container relative-container" ref={inputContainerRef}>
                            <div className="input-wrapper prompt-input-wrapper relative-container">
                                <div className="input-inner-wrapper">
                                    <div
                                        ref={editableRef}
                                        contentEditable={true}
                                        className="input body-regular no-resize colour-primary mention-input-editable"
                                        placeholder={focusChatbotInput === true ? "Disagree with the analysis? Type here to make changes" : "Ask anything about the analysis"}
                                        onInput={handleEditableInput}
                                        onKeyDown={handleEditableKeyDown}
                                        onClick={(e) => {
                                            // Handle clicks on badges - check if target is a badge or inside a badge
                                            const target = e.target;
                                            const badge = target.classList?.contains('mention-badge-inline')
                                                ? target
                                                : target.closest?.('.mention-badge-inline');
                                            
                                            if (badge) {
                                                const address = badge.getAttribute('data-address');
                                                if (address && allComps && allComps.length > 0) {
                                                    // Find the comp with this address
                                                    const comp = allComps.find(c => 
                                                        c.addressDetails.streetAddress === address
                                                    );
                                                    if (comp && viewComp) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        viewComp(comp);
                                                    }
                                                }
                                            }
                                        }}
                                        style={{
                                            minHeight: '36px',
                                            height: 'auto',
                                            maxHeight: '200px',
                                            overflowY: 'auto'
                                        }}
                                        suppressContentEditableWarning={true}
                                    />
                                </div>
                                {
                                    arvDescriptionPrompt.trim() !== "" &&
                                    <IconButton
                                        size="x-small"
                                        icon={<Arrow className="rotate-90 input-prompt-arrow" fill={colour.gray00} />}
                                        disabled={arvDescriptionPrompt.trim() === ""}
                                        clickFunc={() => arvDescriptionPrompt.trim() === "" ? null : submitPrompt()}
                                        standardIcon={false}
                                        background={true}
                                        bgType={"dark"}
                                        contextTooltip={false}
                                        contextTooltipBG={null}
                                        contextTooltipText={null}
                                        customClass={null}
                                    />
                                }
                            </div>
                            {
                                showMentionDropdown && filteredMentionComps.length > 0 &&
                                <div className="small-chat-mention-dropdown-container">
                                    <PredictiveSearch
                                        loading={false}
                                        results={filteredMentionComps.map((item, index) => ({
                                            ...item,
                                            selected: index === selectedMentionIndex
                                        }))}
                                        selectItem={(item) => insertMention(item)}
                                        nested={false}
                                    />
                                </div>
                            }
                        </div>
                    }
                    {
                        readOnly === false && arvEstimate == null && currentLoadingMessage &&
                        <div className="small-chat-text-element">
                            <div className="small-chat-sparkle-container">
                                <Sparkle
                                    fill={colour.gray06}
                                    className=""
                                    gradient={false}
                                />
                            </div>
                            <div className="small-chat-text-content-container small-chat-text-assistant-width">
                                <Typewriter
                                    key={loadingMessageKeyRef.current}
                                    text={currentLoadingMessage}
                                    delay={10}
                                    commaDelay={0}
                                    fullStopDelay={0}
                                    play={true}
                                    className="body-regular colour-secondary block-text"
                                    isHTML={false}
                                    onComplete={() => {}}
                                />
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    )
};

export default React.memo(SmallChat);